import { defineStore, getActivePinia } from "pinia";
import { computed, ref } from "vue";
import { type TemplateSchedulePayload } from "@/features/reporting/interfaces/schedule";
import { type CreateTemplatePayload, type ReportColumn } from "@/features/reporting/interfaces/TemplateInterfaces";
import { Capability, normalisedText, CUSTOMER_FEATURE_FLAG_NAME } from "@/global";
import { useTemplateManagement } from "@/features/reporting/composables/useTemplateManagement";
import { useSystemTemplateBuilderStore } from "@/store/reporting/system-template-builder";
import { FEATURE_NAMES, logError, logEvent, logInfo, logMeasurement } from "@/loggers";
import { DEFAULT_FILTERS } from "@/features/reporting/constants/default-filters";
import { DefaultRange } from "@/features/reporting/constants/default-range";
import { useUserStore } from "@/store/index";
import {
  buildDefaultFilter,
  buildDynamicDefaultFilter,
  isSingleDateFilterCapability,
  computeDateRange,
  isRequiredMrmDateColumn,
  isMrmDisruptionReportType,
  isSubscriptionReportType,
  isSubscriptionMandatoryColumn,
  validateSubscriptionReportColumns,
  SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS
} from "@/features/reporting/utils/common";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CapabilityDefaults = (typeof DEFAULT_FILTERS)[keyof typeof DEFAULT_FILTERS];
type ColumnFilterWithDateMetadata = ReportColumn["filter"] & { frequency?: unknown; dynamicType?: unknown };

// ---------------------------------------------------------------------------
// Pure helpers (no store state dependency)
// ---------------------------------------------------------------------------

const normalizeColumns = (columns: ReportColumn[]): ReportColumn[] => {
  return (columns ?? []).map((col, index) => {
    const sanitized = { ...col } as ReportColumn;
    const filter = sanitized.filter as ColumnFilterWithDateMetadata;
    if (filter && typeof filter === "object") {
      const hasOperator = Boolean(filter.operator && String(filter.operator).trim().length > 0);
      const hasValues = Array.isArray(filter.values) && filter.values.length > 0;
      const hasFilterFunctions = Array.isArray(filter.filterFunctions) && filter.filterFunctions.length > 0;
      const hasOtherProps = Boolean(filter.frequency || filter.dynamicType);

      if (!hasOperator && !hasValues && !hasFilterFunctions && !hasOtherProps) {
        sanitized.filter = null as any;
      }
    }

    return { ...sanitized, order: index + 1 };
  });
};

const hasActiveDateFilter = (col: ReportColumn): boolean => {
  const filter = col.filter;
  if (col.dataType?.toUpperCase() !== "DATE" || !filter) return false;
  return !!filter.operator || (filter.values ?? []).length > 0 || (filter.filterFunctions ?? []).length > 0;
};

const resolveCapabilityDefaults = (capability: Capability | null): CapabilityDefaults => {
  const key = String(capability ?? "default");
  return (DEFAULT_FILTERS as Record<string, CapabilityDefaults>)[key] ?? DEFAULT_FILTERS.default;
};

const resolveDefaultLabel = (
  capability: Capability | null,
  reportType: string,
  capDefaults: CapabilityDefaults
): string => {
  switch (capability) {
    case Capability.PORT_CONGESTION:
    case Capability["D&D"]:
      return String(reportType ?? "")
        .toLowerCase()
        .includes("import")
        ? "ETA"
        : "ETD";
    default:
      return String(capDefaults.defaultLabel ?? "").trim();
  }
};

// buildDefaultFilter moved to shared reporting util

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const BLANK_TEMPLATE: CreateTemplatePayload = {
  name: "",
  description: "",
  capability: null,
  reportType: "",
  columns: [],
  schedules: [],
  type: "USER"
};

export const useTemplateBuilderStore = defineStore("template-builder", () => {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const template = ref<CreateTemplatePayload>({ ...BLANK_TEMPLATE });
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const editColumnsDrawer = ref(false);
  const selectedColumn = ref<ReportColumn | null>(null);
  const suppressDefaultDateInjection = ref(false);
  const defaultFilterApplied = ref(false);

  // -------------------------------------------------------------------------
  // Getters
  // -------------------------------------------------------------------------
  const getTemplatePayload = computed(() => template.value);

  const isBasicInfoValid = computed(
    () => !!template.value.name && template.value.capability !== null && template.value.name.length <= 50
  );

  const isEditColumnsDrawerOpen = computed(() => editColumnsDrawer.value);

  const selectedColumnIdForEditing = computed(
    () => template.value.columns.find((col) => col.id === selectedColumn.value?.id)?.id
  );

  // -------------------------------------------------------------------------
  // Column helpers
  // -------------------------------------------------------------------------

  /**
   * Attempts to inject a default DATE column from the matching system template
   * unless one already exists in the normalised column list.
   */
  const injectDefaultDateColumn = (
    normalized: ReportColumn[],
    effectiveDefaultLabel: string,
    defaultFilter: { operator: string; values: string[]; filterFunctions: string[] },
    dateFormat: string
  ): void => {
    const systemStore = useSystemTemplateBuilderStore();
    const capabilityKey = String(template.value.capability ?? "default");
    const systemTemplate = systemStore.getSystemTemplateByCapability(capabilityKey);

    if (!systemTemplate) return;

    const reportTypeObj =
      systemTemplate.reportTypes.find((rt) => rt.reportType === template.value.reportType) ??
      systemTemplate.reportTypes[0];

    if (!reportTypeObj) return;

    const preferredLabel = effectiveDefaultLabel.toLowerCase();

    const isDateColumn = (c: { dataType?: string; applicableFilterOperators: unknown[] }) =>
      c.dataType?.toUpperCase() === "DATE" && c.applicableFilterOperators.length > 0;

    const allGroups = reportTypeObj.groups;

    // Find the preferred date column by label.
    // For MRM disruption: if not found in the system template, synthesise a stable
    // placeholder so ETA is always injected as a hidden filter regardless.
    // For all other report types: fall back to the first available DATE column.
    let sysCol:
      | { id: string; name: string; dataType: string; dateFormat?: string[]; applicableFilterOperators: string[] }
      | undefined = preferredLabel
      ? allGroups
          .flatMap((g) => g.columns)
          .find(
            (c) =>
              isDateColumn(c) &&
              String(c.name ?? "")
                .trim()
                .toLowerCase() === preferredLabel
          )
      : undefined;

    if (!sysCol) {
      sysCol = allGroups.flatMap((g) => g.columns).find(isDateColumn);
    }

    if (!sysCol) return;

    const sysColId = sysCol.id || `default_date_${Date.now()}`;
    const sysColNameLower = String(sysCol.name ?? "")
      .trim()
      .toLowerCase();

    const matchIndex = normalized.findIndex(
      (c) =>
        c.id === sysColId ||
        String(c.name ?? "")
          .trim()
          .toLowerCase() === sysColNameLower ||
        String(c.label ?? "")
          .trim()
          .toLowerCase() === preferredLabel
    );

    if (matchIndex === -1) {
      // Column not yet in the user's selection — inject as a hidden filter column.
      normalized.push({
        id: sysColId,
        name: String(effectiveDefaultLabel || sysCol.name),
        label: String(effectiveDefaultLabel || sysCol.name || "Date"),
        visibility: "HIDDEN",
        dataType: sysCol.dataType || "DATE",
        order: normalized.length + 1,
        dateFormat: sysCol.dateFormat?.[0] ?? dateFormat,
        filter: defaultFilter
      });
      defaultFilterApplied.value = true;
    } else {
      // Column already in user's selection — apply default filter if none set.
      const existing = normalized[matchIndex];
      if (existing.dataType?.toUpperCase() === "DATE" && !existing.filter) {
        normalized[matchIndex] = { ...existing, filter: defaultFilter };
        defaultFilterApplied.value = true;
      }
    }
  };

  /**
   * For Subscription report type in SHIPMENTS:
   * Ensures Subscribed On, Tolerance Days, and Status columns (with default filters)
   * are present, falling back to declarative configs if system template metadata is absent.
   */
  const injectSubscriptionMandatoryColumns = (columns: ReportColumn[]): ReportColumn[] => {
    if (!isSubscriptionReportType(template.value.reportType)) return columns;

    const systemStore = useSystemTemplateBuilderStore();
    const capabilityKey = String(template.value.capability ?? "default");
    const systemTemplate = systemStore.getSystemTemplateByCapability(capabilityKey);
    const reportTypeObj =
      systemTemplate?.reportTypes?.find((rt) => rt.reportType === template.value.reportType) ??
      systemTemplate?.reportTypes?.[0];

    if (!systemTemplate || !reportTypeObj) {
      logWarn(
        FEATURE_NAMES.MVS_REPORTING,
        `System template metadata missing for capability "${capabilityKey}" / reportType "${template.value.reportType}". Falling back to default mandatory column configs.`
      );
    }

    const allSysCols = reportTypeObj?.groups?.flatMap((g) => g.columns) ?? [];
    const updated = [...columns];

    // Tolerance Days and Status are auto-injected; Subscribed On is added as the date column or user selection
    const configsToInject = SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS.filter((cfg) => cfg.id !== "subscribed_on");

    for (const config of configsToInject) {
      const sysCol = allSysCols.find(config.matcher);
      const colIdx = updated.findIndex(config.matcher);

      let defaultFilter = config.defaultFilter;
      if (defaultFilter && sysCol?.applicableFilterOperators?.length) {
        const preferredOp =
          sysCol.applicableFilterOperators.find((op) => op === defaultFilter?.operator) ||
          sysCol.applicableFilterOperators[0];
        defaultFilter = { ...defaultFilter, operator: preferredOp };
      }

      if (colIdx === -1 && (sysCol || !systemTemplate)) {
        updated.push({
          id: sysCol?.id || config.id || `default_${config.id}_${Date.now()}`,
          name: sysCol?.name || config.name,
          label: sysCol?.name || config.label,
          visibility: "VISIBLE",
          dataType: (sysCol?.dataType as any) || config.dataType,
          order: updated.length + 1,
          filter: defaultFilter || undefined
        });
      } else if (colIdx !== -1 && defaultFilter && !updated[colIdx].filter) {
        updated[colIdx] = { ...updated[colIdx], filter: defaultFilter };
      }
    }

    return updated;
  };

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const setBasicInfoData = (data: { name: string; description: string; capability: Capability | null }) => {
    template.value = { ...template.value, ...data };
  };

  const setColumns = (columns: ReportColumn[]) => {
    let normalized = normalizeColumns(columns);

    const capDefaults = resolveCapabilityDefaults(template.value.capability);
    const effectiveLabel = resolveDefaultLabel(template.value.capability, template.value.reportType, capDefaults);

    // Some capabilities explicitly opt out of default date injection
    if (capDefaults.defaultRange === DefaultRange.None || capDefaults.defaultRange === "none") {
      template.value.columns = normalized;
      return;
    }
    if (suppressDefaultDateInjection.value) {
      template.value.columns = normalized;
      return;
    }

    const dateFormat = String(capDefaults.dateFormat ?? DEFAULT_FILTERS.default?.dateFormat ?? "yyyy-MM-dd");
    const defaultFilter =
      buildDynamicDefaultFilter(capDefaults, template.value.reportType) ??
      buildDefaultFilter(
        computeDateRange(capDefaults, template.value.reportType),
        new Date().toISOString().slice(0, 10)
      );

    const isSameFilter = (a: any, b: any) => {
      if (!a || !b) return false;
      if (a.operator !== b.operator) return false;
      const av = Array.isArray(a.values) ? a.values : [];
      const bv = Array.isArray(b.values) ? b.values : [];
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) {
        if (String(av[i]) !== String(bv[i])) return false;
      }
      const af = Array.isArray(a.filterFunctions) ? a.filterFunctions : [];
      const bf = Array.isArray(b.filterFunctions) ? b.filterFunctions : [];
      if (af.length !== bf.length) return false;
      for (let i = 0; i < af.length; i++) {
        if (String(af[i]) !== String(bf[i])) return false;
      }
      return true;
    };

    const isDefaultFilter = (col: ReportColumn) => !!col.filter && isSameFilter(col.filter as any, defaultFilter);

    // If the incoming columns include a user-added (non-default) date filter,
    // remove only default/injected filters from other date columns and skip injecting a new default.
    const userAddedDateFilterExists = normalized.some((c) => hasActiveDateFilter(c) && !isDefaultFilter(c));

    if (userAddedDateFilterExists) {
      const cleared = normalized.map((c) =>
        c.dataType?.toUpperCase() === "DATE" && isDefaultFilter(c) ? { ...c, filter: undefined } : c
      );
      template.value.columns = cleared;
      return;
    }

    try {
      // MRM disruption reports should not receive a default date filter.
      if (!isMrmDisruptionReportType(template.value.capability, template.value.reportType)) {
        injectDefaultDateColumn(normalized, effectiveLabel, defaultFilter, dateFormat);
      }
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_REPORTING,
        `Error adding default date column from system template: ${err instanceof Error ? err.message : String(err)}`
      );
    }

    // Inject additional default filters declared in DEFAULT_FILTERS.
    // Each AdditionalFilterConfig drives its own column lookup + filter injection.
    const { additionalFilters } = capDefaults;
    if (Array.isArray(additionalFilters) && additionalFilters.length > 0) {
      for (const filterCfg of additionalFilters) {
        const fieldLower = String(filterCfg.field).trim().toLowerCase();
        const operator = String(filterCfg.operator ?? "IN");

        // --- Resolve filter values ---
        let filterValues: string[] = [];
        let resolvedSysCol: any = null;

        if (Array.isArray(filterCfg.values) && filterCfg.values.length > 0) {
          // Strategy 1: static values provided directly in the config.
          filterValues = filterCfg.values.map(String);
        } else if (filterCfg.dataSource) {
          // Strategy 2: dynamic values read from a Pinia store state property.
          const pinia = getActivePinia();
          const rawState = (pinia?.state.value[filterCfg.dataSource.store] as any)?.[filterCfg.dataSource.state];

          if (Array.isArray(rawState)) {
            const sysTemplateMaybe = (rawState as any[]).find(
              (t: any) => String(t.capability) === String(template.value.capability ?? "default")
            );
            const rtObjMaybe = sysTemplateMaybe
              ? (sysTemplateMaybe.reportTypes?.find((rt: any) => rt.reportType === template.value.reportType) ??
                sysTemplateMaybe.reportTypes?.[0])
              : null;
            resolvedSysCol =
              rtObjMaybe?.groups
                ?.flatMap((g: any) => g.columns ?? [])
                .find(
                  (c: any) =>
                    String(c.name ?? "")
                      .trim()
                      .toLowerCase() === fieldLower ||
                    String(c.label ?? "")
                      .trim()
                      .toLowerCase() === fieldLower
                ) ?? null;
            filterValues = resolvedSysCol ? (resolvedSysCol.dataSource?.values ?? []).map(String) : [];
          }
        }

        if (filterValues.length === 0) continue;

        const builtFilter = {
          operator,
          values: filterValues,
          displayableFilter: filterValues.map((e) => normalisedText(e)),
          filterFunctions: []
        };

        const colIdx = normalized.findIndex(
          (c) =>
            String(c.name ?? "")
              .trim()
              .toLowerCase() === fieldLower ||
            String(c.label ?? "")
              .trim()
              .toLowerCase() === fieldLower
        );

        if (colIdx === -1) {
          normalized.push({
            id: resolvedSysCol?.id || `default_filter_${fieldLower}_${Date.now()}`,
            name: String(filterCfg.field),
            label: String(filterCfg.field),
            visibility: "HIDDEN",
            dataType: resolvedSysCol?.dataType || "STRING",
            order: normalized.length + 1,
            filter: builtFilter
          });
        } else if (!normalized[colIdx].filter) {
          normalized[colIdx] = { ...normalized[colIdx], filter: builtFilter };
        }
      }
    }

    try {
      normalized = injectSubscriptionMandatoryColumns(normalized);
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_REPORTING,
        `Error injecting subscription mandatory columns: ${err instanceof Error ? err.message : String(err)}`
      );
    }

    template.value.columns = normalized;
  };

  const initializeForm = (data: CreateTemplatePayload) => {
    template.value = { ...data };
    suppressDefaultDateInjection.value = false;
    defaultFilterApplied.value = false;
  };

  const resetState = () => {
    template.value = { ...BLANK_TEMPLATE };
    error.value = null;
    isLoading.value = false;
    defaultFilterApplied.value = false;
  };

  // -------------------------------------------------------------------------
  // Schedule actions
  // -------------------------------------------------------------------------

  const scheduleWithId = (schedule: TemplateSchedulePayload): TemplateSchedulePayload => ({
    ...schedule,
    templateId: template.value.id ?? ""
  });

  const addSchedule = (schedule: TemplateSchedulePayload) => {
    const current = template.value.schedules ?? [];
    template.value = { ...template.value, schedules: [...current, scheduleWithId(schedule)] };
  };

  const removeSchedule = (index: number) => {
    const current = template.value.schedules ?? [];
    if (index < 0 || index >= current.length) return;
    template.value = { ...template.value, schedules: current.filter((_, i) => i !== index) };
  };

  const updateSchedule = (index: number, schedule: TemplateSchedulePayload) => {
    const current = template.value.schedules ?? [];
    if (index < 0 || index >= current.length) return;
    const next = [...current];
    next[index] = scheduleWithId(schedule);
    template.value = { ...template.value, schedules: next };
  };

  const updateScheduleStatus = (index: number, status: string) => {
    const current = template.value.schedules ?? [];
    if (index < 0 || index >= current.length) return;
    template.value = {
      ...template.value,
      schedules: current.map((s, i) => (i === index ? { ...s, status } : s))
    };
  };

  // -------------------------------------------------------------------------
  // Template persistence
  // -------------------------------------------------------------------------

  const hasAnyActiveDateFilter = (): boolean => {
    return (template.value.columns ?? []).some(hasActiveDateFilter);
  };

  const ensureDefaultDateFilterBeforeSave = async () => {
    // Only auto-inject default filter if feature flag is enabled
    const userStore = useUserStore();
    if (
      !userStore.isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_CENTRALISED_REPORTING_FAST_FOLLOW_UP)
    ) {
      return;
    }

    if (!hasAnyActiveDateFilter()) {
      suppressDefaultDateInjection.value = false;
      setColumns(template.value.columns ?? []);
      return;
    }

    if (template.value.capability === Capability.MAERSK_RISK_MANAGEMENT) {
      const activeDateCols = (template.value.columns ?? []).filter(hasActiveDateFilter);

      if (activeDateCols.length > 0 && !activeDateCols.some(isRequiredMrmDateColumn)) {
        const normalized = normalizeColumns(template.value.columns ?? []);
        const capDefaults = resolveCapabilityDefaults(template.value.capability);
        const effectiveLabel = resolveDefaultLabel(template.value.capability, template.value.reportType, capDefaults);
        const dateFormat = String(capDefaults.dateFormat ?? DEFAULT_FILTERS.default?.dateFormat ?? "yyyy-MM-dd");
        const defaultFilter =
          buildDynamicDefaultFilter(capDefaults, template.value.reportType) ??
          buildDefaultFilter(
            computeDateRange(capDefaults, template.value.reportType),
            new Date().toISOString().slice(0, 10)
          );

        injectDefaultDateColumn(normalized, effectiveLabel, defaultFilter, dateFormat);
        template.value.columns = normalized;
      }
    }

    if (isSubscriptionReportType(template.value.reportType)) {
      const normalized = normalizeColumns(template.value.columns ?? []);
      template.value.columns = injectSubscriptionMandatoryColumns(normalized);
    }
  };

  const validateSubscriptionColumns = (columns = template.value.columns ?? []) => {
    if (!isSubscriptionReportType(template.value.reportType)) {
      return { isValid: true, missingColumns: [], invalidFilters: [] };
    }
    return validateSubscriptionReportColumns(columns);
  };

  const saveTemplate = async () => {
    const start = performance.now();
    const { name, capability, reportType } = template.value;
    logInfo(
      FEATURE_NAMES.MVS_REPORTING,
      `Saving template — name: ${name}, capability: ${capability}, reportType: ${reportType}`
    );

    isLoading.value = true;
    error.value = null;

    try {
      await ensureDefaultDateFilterBeforeSave();
      if (isSubscriptionReportType(template.value.reportType)) {
        const val = validateSubscriptionColumns();
        if (!val.isValid) {
          throw new Error(
            "Subscription report requires Subscribed On, Tolerance Days, and Status fields with valid filters."
          );
        }
      }
      await useTemplateManagement().saveTemplate(template.value);
      logEvent(FEATURE_NAMES.MVS_REPORTING, "template_save_success", { templateName: name, capability, reportType });
      resetState();
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_REPORTING,
        `Error saving template: ${err instanceof Error ? err.message : String(err)}`
      );
      throw err;
    } finally {
      isLoading.value = false;
      const elapsed = performance.now() - start;
      logInfo(FEATURE_NAMES.MVS_REPORTING, `Template save completed in ${elapsed} ms.`);
      logMeasurement(FEATURE_NAMES.MVS_REPORTING, "template_save_latency", elapsed, {
        api: "saveTemplate",
        templateName: name,
        capability,
        reportType
      });
    }
  };

  // -------------------------------------------------------------------------
  // Column drawer / editing
  // -------------------------------------------------------------------------

  const toggleEditColumnsDrawer = (isOpen: boolean) => {
    editColumnsDrawer.value = isOpen;
  };

  const setSelectedColumnForEditing = (column: ReportColumn) => {
    const clone = JSON.parse(JSON.stringify(column));
    // Default dateFormat to "dd-MM-yyyy" for DATE columns when not already set
    if (clone.dataType?.toUpperCase() === "DATE" && !clone.dateFormat) {
      clone.dateFormat = "dd-MM-yyyy";
    }
    selectedColumn.value = clone;
  };

  const updatedEditedColumn = (updatedColumn: ReportColumn) => {
    selectedColumn.value = { ...updatedColumn };
  };

  const setEditedColumnsToTemplate = () => {
    if (!selectedColumn.value) return;

    let updatedColumns = template.value.columns.map((col) =>
      col.id === selectedColumn.value?.id ? selectedColumn.value! : col
    );

    // For EMISSIONS and MRM: only one date filter may be active within the "Dates" group.
    const cap = template.value.capability as Capability;
    const isTargetCapability = isSingleDateFilterCapability(cap);

    // Compute default filter for comparison so we only clear filters that
    // were injected as the default (don't remove user-added filters).
    const capDefaults = resolveCapabilityDefaults(template.value.capability);
    const defaultFilter =
      buildDynamicDefaultFilter(capDefaults, template.value.reportType) ??
      buildDefaultFilter(
        computeDateRange(capDefaults, template.value.reportType),
        new Date().toISOString().slice(0, 10)
      );
    const isSameFilter = (a: any, b: any) => {
      if (!a || !b) return false;
      if (a.operator !== b.operator) return false;
      const av = Array.isArray(a.values) ? a.values : [];
      const bv = Array.isArray(b.values) ? b.values : [];
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) {
        if (String(av[i]) !== String(bv[i])) return false;
      }
      const af = Array.isArray(a.filterFunctions) ? a.filterFunctions : [];
      const bf = Array.isArray(b.filterFunctions) ? b.filterFunctions : [];
      if (af.length !== bf.length) return false;
      for (let i = 0; i < af.length; i++) {
        if (String(af[i]) !== String(bf[i])) return false;
      }
      return true;
    };
    const isDefaultFilter = (col: ReportColumn) => !!col.filter && isSameFilter(col.filter as any, defaultFilter);

    if (isTargetCapability && selectedColumn.value.dataType?.toUpperCase() === "DATE") {
      try {
        const systemStore = useSystemTemplateBuilderStore();
        const sysTemplate = systemStore.getSystemTemplateByCapability(String(cap));
        const reportTypeObj = sysTemplate?.reportTypes.find((rt) => rt.reportType === template.value.reportType);
        const datesGroup = reportTypeObj?.groups?.find((g) => g.groupName === "Dates");
        const datesIds = new Set(datesGroup?.columns?.map((c) => c.id) ?? []);

        if (datesIds.size > 0 && datesIds.has(selectedColumn.value.id)) {
          const selectedHasFilter = !!(
            selectedColumn.value.filter &&
            (selectedColumn.value.filter.operator ||
              (Array.isArray(selectedColumn.value.filter.values) && selectedColumn.value.filter.values.length > 0) ||
              (Array.isArray(selectedColumn.value.filter.filterFunctions) &&
                selectedColumn.value.filter.filterFunctions.length > 0))
          );

          if (selectedHasFilter) {
            // Clear ALL other date filters in the "Dates" group (both default and user-set)
            // to enforce the single-date-filter rule for EMISSIONS and MRM.
            updatedColumns = updatedColumns.map((col) =>
              col.id !== selectedColumn.value!.id && datesIds.has(col.id) && col.dataType?.toUpperCase() === "DATE"
                ? { ...col, filter: undefined }
                : col
            );
          }
        }
      } catch (err) {
        logError(
          FEATURE_NAMES.MVS_REPORTING,
          `Error enforcing single-date-filter: ${err instanceof Error ? err.message : String(err)}`
        );
      }
    }
    try {
      const isAddingDateFilter =
        selectedColumn.value.dataType?.toUpperCase() === "DATE" &&
        !!(
          selectedColumn.value.filter &&
          (selectedColumn.value.filter.operator ||
            (Array.isArray(selectedColumn.value.filter.values) && selectedColumn.value.filter.values.length > 0) ||
            (Array.isArray(selectedColumn.value.filter.filterFunctions) &&
              selectedColumn.value.filter.filterFunctions.length > 0))
        );

      if (isAddingDateFilter) {
        updatedColumns = updatedColumns.map((col) =>
          col.id !== selectedColumn.value.id && col.dataType?.toUpperCase() === "DATE"
            ? isDefaultFilter(col)
              ? { ...col, filter: undefined }
              : col
            : col
        );
        // Since the user added a non-default date filter, mark default as not applied
        defaultFilterApplied.value = false;
      }
    } catch (err) {
      logError(
        FEATURE_NAMES.MVS_REPORTING,
        `Error clearing injected default date filter: ${err instanceof Error ? err.message : String(err)}`
      );
    }

    setColumns(updatedColumns);
  };

  const resetEditedColumn = () => {
    if (!selectedColumn.value) return;

    if (isSubscriptionReportType(template.value.reportType) && isSubscriptionMandatoryColumn(selectedColumn.value)) {
      const config = SUBSCRIPTION_MANDATORY_COLUMN_CONFIGS.find((cfg) => cfg.matcher(selectedColumn.value));
      const defaultFilter = config?.defaultFilter ?? undefined;

      selectedColumn.value = {
        ...selectedColumn.value,
        label: selectedColumn.value.name || "",
        visibility: "VISIBLE",
        filter: defaultFilter,
        dateFormat: selectedColumn.value.dataType?.toUpperCase() === "DATE" ? "dd-MM-yyyy" : undefined
      };
      return;
    }

    // Clear all editable fields to their default/empty state
    // This applies to both create and edit modes
    selectedColumn.value = {
      ...selectedColumn.value,
      label: selectedColumn.value.name || "", // Reset label to column name
      visibility: "VISIBLE",
      filter: undefined,
      dateFormat: undefined
    };
  };

  const removeColumnFromTemplate = (columnId: string) => {
    const existing = (template.value.columns ?? []).find((c) => c.id === columnId);
    if (existing && isSubscriptionMandatoryColumn(existing, template.value.reportType)) {
      return;
    }
    if (existing && existing.dataType?.toUpperCase() === "DATE") {
      suppressDefaultDateInjection.value = true;
    }
    const updatedColumns = (template.value.columns ?? [])
      .filter((col) => col.id !== columnId)
      .map((col, index) => ({ ...col, order: index + 1 }));
    setColumns(updatedColumns);
  };

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  return {
    // State
    template,
    isLoading,
    error,
    editColumnsDrawer,
    selectedColumn,
    defaultFilterApplied,
    // Getters
    getTemplatePayload,
    isBasicInfoValid,
    isEditColumnsDrawerOpen,
    selectedColumnIdForEditing,
    // Actions
    setBasicInfoData,
    setColumns,
    initializeForm,
    addSchedule,
    removeSchedule,
    updateSchedule,
    updateScheduleStatus,
    saveTemplate,
    validateSubscriptionColumns,
    resetState,
    toggleEditColumnsDrawer,
    setSelectedColumnForEditing,
    updatedEditedColumn,
    setEditedColumnsToTemplate,
    resetEditedColumn,
    removeColumnFromTemplate
  };
});
